import { FC, useContext, useMemo } from 'react';
import { Button, Cascader, Dropdown, MenuProps, Modal, Select, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { StopPropagation } from '@shared/components/stop-propagation';
import { CheckCell, OverCell, Overflow } from '@shared/components/cells';
import { nextTableColumns, nextTableColumnGenerator } from '@shared/modules/next-table/helpers/next-table-columns';
import { NextTableCheckboxFilter } from '@shared/modules/next-table/components/filters/checkbox';
import { useAuth } from '@modules/me';
import { getCurrencySymbol } from '@shared/utils';
import { UserSelectFilter } from '@modules/users';
import { ICashFlowTransaction } from '../../interfaces';
import { CashFlowTransactionsTableContext } from '../../context';
import { CashFlowTransactionsService } from '../../services';
import { cashFlowTransactionQueryKeys } from '../../utils';
import { useCashRegisters } from '../cashRegisters';
import { useCashRegisterOperationsWithSub } from '../cashRegisterOperations';
import { filterOption } from '@shared/modules/form';

const ExecutorFilter: FC<any> = (props) => <UserSelectFilter {...props} query={{ admin: 1 }} preload />;

export const useCashFlowTransactionsTableColumns = (): Column<ICashFlowTransaction>[] => {
  const { handleFetch } = useContext(CashFlowTransactionsTableContext);
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data: cashRegisters = [] } = useCashRegisters();
  const { data: operations = [] } = useCashRegisterOperationsWithSub();

  const operationOptions = useMemo(() => operations.map((op) => ({ value: op.id, label: op.name, children: op.children.map((child) => ({ value: child.id, label: child.name })) })), [operations]);

  const actionsColumn = useMemo<Column<ICashFlowTransaction>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [
          {
            key: 'details',
            label: 'Ətraflı bax',
            icon: <Icons.FileTextOutlined />,
            onClick: () => navigate(`/cash-flow/transactions/${original.id}`),
          },
          {
            key: 'edit',
            label: 'Düzəliş et',
            icon: <Icons.EditOutlined />,
            disabled: !can('cashbox_operations.cud'),
            onClick: () => navigate(`/cash-flow/transactions/${original.id}/update`, { state: { background: window.location.pathname } }),
          },
          { type: 'divider' as const },
          {
            key: 'delete',
            label: 'Sil',
            icon: <Icons.DeleteOutlined />,
            danger: true,
            disabled: !can('cashbox_operations.cancel_cashflow_transaction'),
            onClick: () =>
              Modal.confirm({
                title: 'Diqqət',
                content: 'Tranzaksiyanı silməyə əminsinizmi?',
                okType: 'danger',
                onOk: async () => {
                  const result = await CashFlowTransactionsService.remove(original.id);
                  if (result.status === 200) {
                    message.success('Tranzaksiya silindi.');
                    handleFetch();
                  } else message.error(result.data as string);
                },
              }),
          },
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [can, handleFetch, navigate],
  );

  const baseColumns = useMemo<Column<ICashFlowTransaction>[]>(
    () => [
      { ...nextTableColumns.small, Header: 'Kod', id: cashFlowTransactionQueryKeys.id, accessor: (r) => r.id },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.cashRegister.name,
        id: cashFlowTransactionQueryKeys.cashRegister.id,
        Header: 'Kassa',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear showSearch filterOption={filterOption} optionFilterProp="children" style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            {cashRegisters.map((c) => (
              <Select.Option key={c.id} value={c.id.toString()}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.small,
        accessor: (r) => r.amount,
        id: cashFlowTransactionQueryKeys.amount,
        Header: 'Məbləğ',
        Cell: ({ row: { original } }: any) => `${original.amount.toFixed(2)} ${getCurrencySymbol(original.cashRegister.currency.code)}` as any,
      },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.type,
        id: cashFlowTransactionQueryKeys.type,
        Header: 'Əməliyyat',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            <Select.Option value="1">Mədaxil</Select.Option>
            <Select.Option value="2">Məxaric</Select.Option>
          </Select>
        ),
        Cell: ({ cell: { value } }: any) => (value === 'income' ? 'Mədaxil' : value === 'expense' ? 'Məxaric' : null),
      },
      {
        accessor: (r) => r.paymentType.name,
        id: cashFlowTransactionQueryKeys.paymentType.id,
        Header: 'Ödəniş tipi',
        Cell: OverCell,
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            <Select.Option value="1">Nağd</Select.Option>
            <Select.Option value="3">Terminal</Select.Option>
            <Select.Option value="8">Hədiyyə</Select.Option>
          </Select>
        ),
      },
      {
        ...nextTableColumnGenerator(50),
        accessor: (r) => r.isTransfer,
        id: cashFlowTransactionQueryKeys.isTransfer,
        Header: 'TR',
        Filter: NextTableCheckboxFilter,
        Cell: CheckCell,
      },
      {
        accessor: (r) => r.operation.name,
        id: cashFlowTransactionQueryKeys.operation.child.id,
        Header: 'Kateqoriya',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Cascader placeholder="" options={operationOptions} allowClear style={{ width: '100%' }} changeOnSelect value={filterValue} onChange={setFilter} />
        ),
        Cell: ({ row: { original } }: any) => (
          <Overflow>
            {original.operation.name} - {original.operation.child.name}
          </Overflow>
        ),
      },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.executor.name,
        id: cashFlowTransactionQueryKeys.executor.id,
        Header: 'İcraçı',
        Cell: OverCell,
        Filter: ExecutorFilter,
      },
      { ...nextTableColumns.date, accessor: (r) => r.operatedAt, id: cashFlowTransactionQueryKeys.operatedAt, Header: 'Əməliyyat tarixi' },
    ],
    [cashRegisters, operationOptions],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
